import { Link } from 'react-router-dom';
import { AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import { cn } from '@/lib/cn';
import type { AlertLevel, DashboardAlert } from './selectors';

function iconFor(level: AlertLevel) {
    if (level === 'danger') return AlertCircle;
    if (level === 'warn') return AlertTriangle;
    return Info;
}

function toneClasses(level: AlertLevel): { icon: string; bar: string } {
    if (level === 'danger') return { icon: 'text-error-text', bar: 'bg-error' };
    if (level === 'warn') return { icon: 'text-warning-text', bar: 'bg-warning' };
    return { icon: 'text-info-text', bar: 'bg-info' };
}

function AlertRow({ alert }: { alert: DashboardAlert }) {
    const Icon = iconFor(alert.level);
    const tone = toneClasses(alert.level);
    const inner = (
        <div className="flex items-stretch gap-3 py-2">
            <div className={cn('w-1 rounded-full shrink-0', tone.bar)} aria-hidden />
            <Icon className={cn('w-4 h-4 mt-0.5 shrink-0', tone.icon)} aria-hidden />
            <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-text">{alert.title}</div>
                <div className="text-xs text-text-subtle">{alert.description}</div>
            </div>
        </div>
    );

    if (alert.href) {
        return (
            <li className="border-b border-border last:border-b-0">
                <Link
                    to={alert.href}
                    className="block px-1 -mx-1 rounded hover:bg-surface-muted transition-colors duration-[var(--duration-base)]"
                >
                    {inner}
                </Link>
            </li>
        );
    }
    return <li className="border-b border-border last:border-b-0 px-1 -mx-1">{inner}</li>;
}

export function AlertsPanel({ alerts }: { alerts: DashboardAlert[] }) {
    return (
        <Card>
            <CardHeader>
                <CardTitle>Alerts</CardTitle>
                {alerts.length > 0 && (
                    <span className="text-xs text-text-subtle tabular-nums">
                        {alerts.length}
                    </span>
                )}
            </CardHeader>
            {alerts.length === 0 ? (
                <p className="text-sm text-text-subtle py-4 text-center">
                    No alerts — everything looks good.
                </p>
            ) : (
                <ul className="flex flex-col">
                    {alerts.map((a) => (
                        <AlertRow key={a.id} alert={a} />
                    ))}
                </ul>
            )}
        </Card>
    );
}
